import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import {
  Modal,
  Tabs,
  Box,
  Group,
  Text,
  Button,
  Table,
  Loader,
  Progress,
  Alert,
  Collapse,
  ActionIcon,
} from '@mantine/core';
import {
  IconDatabase,
  IconFolder,
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangle,
} from '@tabler/icons-react';

function formatFileSize(bytes) {
  if (bytes == null || isNaN(bytes) || bytes === 0) return '0 B';
  const num = Number(bytes);
  if (num < 1024) return num + ' B';
  if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB';
  if (num < 1024 * 1024 * 1024) return (num / (1024 * 1024)).toFixed(2) + ' MB';
  return (num / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

class SchoolUserRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      loading: false,
      perEpack: null,
    };
    autoBind(this);
  }

  handleToggle() {
    const { open, perEpack } = this.state;
    if (!open && perEpack === null) {
      this.fetchUserEpacks();
    }
    this.setState({ open: !open });
  }

  fetchUserEpacks() {
    const { root, row } = this.props;
    if (!root || !root.refs || !root.refs.API || !row.user_id) return;

    this.setState({ loading: true });
    root.refs.API.request(
      "get_user_storage_summary",
      { user_id: row.user_id },
      (res) => {
        const data = res && res.body ? res.body.result : null;
        this.setState({ perEpack: data ? data.per_epack : [], loading: false });
      },
      () => {
        this.setState({ loading: false, perEpack: [] });
      }
    );
  }

  render() {
    const { row } = this.props;
    const { open, loading, perEpack } = this.state;

    return (
      <React.Fragment>
        <Table.Tr>
          <Table.Td style={{ width: 40 }}>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={this.handleToggle}>
              {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionIcon>
          </Table.Td>
          <Table.Td>{row.display_name || row.username}</Table.Td>
          <Table.Td>{row.username}</Table.Td>
          <Table.Td style={{ textAlign: 'right' }}>{row.file_count}</Table.Td>
          <Table.Td style={{ textAlign: 'right' }}>{row.epack_count != null ? row.epack_count : '-'}</Table.Td>
          <Table.Td style={{ textAlign: 'right' }}>{formatFileSize(row.total_size)}</Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td colSpan={6} style={{ padding: 0, border: open ? undefined : 'none' }}>
            <Collapse in={open}>
              <Box style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 48 }}>
                {loading && (
                  <Group justify="center" py="md"><Loader size="sm" /></Group>
                )}
                {!loading && perEpack && perEpack.length > 0 && (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{Localization.strings.EPACK_NAME}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.FILE_COUNT}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.TOTAL_SIZE}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {perEpack.map((ep) => (
                        <Table.Tr key={ep.epack_id}>
                          <Table.Td>
                            <Group gap={6} wrap="nowrap">
                              <IconFolder size={16} color="var(--mantine-color-gray-6)" />
                              {ep.epack_name}
                            </Group>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{ep.file_count}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{formatFileSize(ep.total_size)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                {!loading && perEpack && perEpack.length === 0 && (
                  <Text size="sm" c="dimmed" py={4}>{Localization.strings.NO_FILES}</Text>
                )}
              </Box>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      </React.Fragment>
    );
  }
}

class StorageInfoDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      tabIndex: 0,
      userLoading: false,
      schoolLoading: false,
      userStorage: null,
      schoolStorage: null,
    };
    autoBind(this);
  }

  show() {
    this.setState({ open: true, tabIndex: 0, userStorage: null, schoolStorage: null });
    this.fetchUserStorage();
    this.fetchSchoolStorage();
  }

  hide() {
    this.setState({ open: false });
  }

  fetchUserStorage() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.API) return;
    const userId = root.getMyUserId();
    if (!userId) return;

    this.setState({ userLoading: true });
    root.refs.API.request(
      "get_user_storage_summary",
      { user_id: userId },
      (res) => {
        const data = res && res.body ? res.body.result : null;
        this.setState({ userStorage: data, userLoading: false });
      },
      () => {
        this.setState({ userLoading: false });
      }
    );
  }

  fetchSchoolStorage() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.API) return;
    const schoolId = root.getMySchoolId();
    if (!schoolId) return;

    this.setState({ schoolLoading: true });
    root.refs.API.request(
      "get_school_users_storage",
      { school_id: schoolId },
      (res) => {
        const data = res && res.body ? res.body.result : null;
        this.setState({ schoolStorage: data, schoolLoading: false });
      },
      () => {
        this.setState({ schoolLoading: false });
      }
    );
  }

  getStorageLimit() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.userDataProfile) return null;
    const privilege = root.refs.userDataProfile.getPrivilege('STORAGE_LIMIT');
    return privilege && privilege.value ? Number(privilege.value) : null;
  }

  getSchoolName() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.userDataProfile) return '';
    const schoolInfo = root.refs.userDataProfile.state.schoolInfo;
    if (!schoolInfo) return '';
    if (Localization.strings.getLanguage() === 'en' && schoolInfo.name_eng) {
      return schoolInfo.name_eng;
    }
    return schoolInfo.name || schoolInfo.name_eng || '';
  }

  renderUserStorage() {
    const { userStorage, userLoading } = this.state;

    if (userLoading) {
      return <Group justify="center" py="xl"><Loader /></Group>;
    }

    if (!userStorage) {
      return <Text ta="center" py="xl" c="dimmed">{Localization.strings.NO_FILES}</Text>;
    }

    const { file_count, total_size, per_epack } = userStorage;

    return (
      <Box>
        <Group gap="md" mb="md" p="md" style={{ backgroundColor: '#f1f3f5', borderRadius: 8 }}>
          <IconDatabase size={40} color="var(--mantine-color-brand-6)" />
          <Box>
            <Text fw={600} size="lg">{formatFileSize(total_size)}</Text>
            <Text size="sm" c="dimmed">{file_count} {Localization.strings.FILE_COUNT}</Text>
          </Box>
        </Group>

        {per_epack && per_epack.length > 0 && (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{Localization.strings.EPACK_NAME}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.FILE_COUNT}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.TOTAL_SIZE}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {per_epack.map((row) => (
                <Table.Tr key={row.epack_id}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <IconFolder size={16} color="var(--mantine-color-gray-6)" />
                      {row.epack_name}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{row.file_count}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatFileSize(row.total_size)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {(!per_epack || per_epack.length === 0) && file_count === 0 && (
          <Text size="sm" ta="center" py="md" c="dimmed">{Localization.strings.NO_FILES}</Text>
        )}
      </Box>
    );
  }

  renderSchoolStorage() {
    const { schoolStorage, schoolLoading } = this.state;

    if (schoolLoading) {
      return <Group justify="center" py="xl"><Loader /></Group>;
    }

    if (!schoolStorage) {
      return <Text ta="center" py="xl" c="dimmed">{Localization.strings.NO_FILES}</Text>;
    }

    const { school_total_size, school_file_count, users } = schoolStorage;
    const storageLimit = this.getStorageLimit();
    const schoolName = this.getSchoolName();

    const storageLimitBytes = storageLimit != null ? storageLimit * 1024 * 1024 : null;
    const usagePercent = storageLimitBytes ? Math.min((school_total_size / storageLimitBytes) * 100, 100) : 0;
    const isAtLimit = storageLimitBytes != null && school_total_size >= storageLimitBytes;
    const progressColor = usagePercent >= 90 ? 'red' : usagePercent >= 70 ? 'yellow' : 'brand';

    return (
      <Box>
        <Group gap="md" mb="md" p="md" align="flex-start" style={{ backgroundColor: '#f1f3f5', borderRadius: 8 }}>
          <IconDatabase size={40} color="var(--mantine-color-brand-6)" />
          <Box style={{ flex: 1 }}>
            {schoolName && (
              <Text size="sm" c="dimmed" fw={500}>{schoolName}</Text>
            )}
            <Text fw={600} size="lg">{formatFileSize(school_total_size)}</Text>
            <Text size="sm" c="dimmed">{school_file_count} {Localization.strings.FILE_COUNT}</Text>
            {storageLimit != null && (
              <Box mt="sm">
                <Group justify="space-between" mb={4}>
                  <Text size="sm" c="dimmed">{Localization.strings.STORAGE_USAGE}</Text>
                  <Text size="sm" c="dimmed">
                    {formatFileSize(school_total_size)} / {(storageLimit / 1024).toFixed(2)} GB
                  </Text>
                </Group>
                <Progress value={usagePercent} color={progressColor} size="lg" radius="xl" />
                <Text size="xs" c="dimmed" mt={4}>{usagePercent.toFixed(1)}%</Text>
              </Box>
            )}
          </Box>
        </Group>

        {isAtLimit && (
          <Alert color="yellow" icon={<IconAlertTriangle size={18} />} mb="md">
            {Localization.strings.STORAGE_LIMIT_WARNING}
          </Alert>
        )}

        {users && users.length > 0 && (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }} />
                <Table.Th>{Localization.strings.USER_NAME}</Table.Th>
                <Table.Th>{Localization.strings.USER_LOGIN_NAME}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.FILE_COUNT}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.LOCALE_EPACK_COUNT}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{Localization.strings.TOTAL_SIZE}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((row) => (
                <SchoolUserRow
                  key={row.user_id}
                  row={row}
                  root={this.props.root}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}

        {(!users || users.length === 0) && (
          <Text size="sm" ta="center" py="md" c="dimmed">{Localization.strings.NO_FILES}</Text>
        )}
      </Box>
    );
  }

  render() {
    const { open, tabIndex } = this.state;

    return (
      <Modal
        opened={open}
        onClose={this.hide}
        title={<Text fw={600}>{Localization.strings.STORAGE_INFO}</Text>}
        size="lg"
        centered
        zIndex={2000}
      >
        <Tabs value={String(tabIndex)} onChange={(v) => this.setState({ tabIndex: Number(v) })}>
          <Tabs.List grow mb="md">
            <Tabs.Tab value="0">{Localization.strings.MY_STORAGE}</Tabs.Tab>
            <Tabs.Tab value="1">{Localization.strings.SCHOOL_STORAGE}</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Box>
          {tabIndex === 0 && this.renderUserStorage()}
          {tabIndex === 1 && this.renderSchoolStorage()}
        </Box>

        <Group justify="flex-end" mt="lg">
          <Button onClick={this.hide}>{Localization.strings.CLOSE}</Button>
        </Group>
      </Modal>
    );
  }
}

export default StorageInfoDialog;
